import { ReactNode } from "react";
import FaqItem from "./FaqItem";
import styles from "./FaqSection.module.css";

import {
  IconBrain,
  IconClock24,
  IconHeart,
  IconListDetails,
  IconLock,
  IconScale,
  IconShieldCheck,
  IconSparkles,
  IconTargetArrow,
} from "@tabler/icons-react";

export interface FaqEntry {
  question: string;
  answer: string;
  icon: ReactNode;
}

export const defaultFaqData: FaqEntry[] = [
  /* The first two questions mirror, VERBATIM, the "People also ask" boxes
     Google shows on our brand SERP ("Is Aura AI free?", "Is the Aura AI app
     safe?"). Answer text must stay honest and match the pricing page and the
     Play listing; it is quoted by AI Overviews. */
  {
    question: "Is Aura AI free?",
    answer:
      "Yes, to start. Your kundli is free, the daily horoscope is free, and your first question to the AI astrologer is free, with no card needed. After the free start, chats are paid from wallet credits, so you only pay for what you use; there is no per-minute meter, and an optional subscription is available on the pricing page.",
    icon: <IconBrain size={20} />,
  },
  {
    question: "Is the Aura AI app safe?",
    answer:
      "Yes. The Android app (Aura AI: AI Astrology & Kundli, rated 4.4 on Google Play with 1M+ downloads) signs you in with a phone OTP, never a password, and your conversations are encrypted in transit and at rest. Your birth details stay private, are never sold, and predictions are guidance for reflection, not medical, legal or financial advice.",
    icon: <IconBrain size={20} />,
  },
  {
    question: "What is Aura AI?",
    answer:
      "Aura AI takes the old practice of Vedic astrology and gives it a modern engine. Instead of short, vague horoscopes, it offers clear advice you can act on. It shows you what might happen, when it could happen and why. Alongside every prediction you get a simple note about the astrological principle behind it, so you’re not just taking our word for it. We built the platform from a huge base of classical texts and 1M+ real charts. The aim is to give anyone, anywhere, a personal map of their life that’s both rooted in tradition and sharpened by technology.",
    icon: <IconBrain size={20} />,
  },
  {
    question: "How Accurate are the results?",
    answer:
      "Accuracy starts with the chart itself. Your kundli is computed from Swiss Ephemeris astronomical data, built on NASA JPL ephemerides, so every planetary degree is precise, and the reading includes details most apps skip: Gulika and Mandi positions, and a deep yoga analysis covering more than 1,000 yogas. On top of that chart, the system has learned from 1M+ anonymised birth charts and thousands of classical rules, letting it see patterns too complex for any single astrologer to track manually. Rather than saying “something may happen soon,” it points to the specific future windows that look strongest for you and explains the astrological reasoning behind each one, so you can judge it before you act on it.",
    icon: <IconTargetArrow size={20} />,
  },
  {
    question: "Why AI Astrology?",
    answer:
      "Choosing the right astrologer can be confusing - Aura AI removes the guesswork. It combines thousands of classical Jyotish rules, 1M+ historical birth charts, and advanced AI models to deliver consistent, transparent guidance. Your first question is free, chat is never billed per minute, and there are no hidden fees: after the free start, chats are paid from wallet credits, so you only pay for what you actually use, and regular users can take an optional subscription from the pricing page. This means you get a reliable, always-available astrologer-like experience grounded in your own chart.",
    icon: <IconShieldCheck size={20} />,
  },
  {
    question: "Why Aura AI is the Right Choice?",
    answer:
      "When people talk about Aura AI, they don’t just mention “accuracy” or “algorithms.” They talk about how it feels - a mix of old-world astrology and modern clarity. Someone might open the app before a job interview; another might check it late at night to understand a relationship pattern. What they all notice is that it doesn’t sound like a horoscope column - it sounds like someone who actually knows them.",
    icon: <IconHeart size={20} />,
  },
  {
    question: "Is AI astrology more accurate than human astrologers?",
    answer:
      "They are good at different things. A human astrologer brings intuition, context and the ability to read your situation as a whole. Aura AI brings breadth and consistency: it reads over 100 astrological variables at once, more than any single astrologer can hold in mind simultaneously, and it condenses thousands of classical Jyotish rules into one system. Because it learns from 1M+ charts, it keeps getting sharper over time, and because it is built on validated astrological principles rather than guesswork, two people asking the same question of the same chart get the same reasoning. The honest summary: AI wins on pattern recognition and consistency, humans win on nuance and empathy.",
    icon: <IconScale size={20} />,
  },
  {
    question: "How do AI astrology tools ensure data privacy and security?",
    answer:
      "Your birth details and conversations are treated as private by default. Aura AI encrypts your conversations in transit and at rest, stores birth data in anonymised form, and follows strict privacy policies; your details are never sold. Practically, that means the chat is a safe, private and encrypted space where you can speak without judgment, which matters because people ask this AI the questions they would hesitate to ask another person: about a marriage that is struggling, money they have lost, or a health worry they have told no one. Nothing you type is used to identify you publicly, and you can ask anything at any hour without a queue, an audience, or a record that follows you around.",
    icon: <IconLock size={20} />,
  },
  {
    question:
      "What factors determine the accuracy of astrological predictions?",
    answer:
      "Birth data accuracy, astrological method, and interpretation style all matter. Aura AI relies on pattern recognition and calibration, while humans add nuance. The reader’s intent and belief also shape how predictions are received.",
    icon: <IconListDetails size={20} />,
  },
  {
    question: "When is help available?",
    answer:
      "We are available for your help when you actually need it. Whether it’s midnight in Mumbai or dawn in Chicago, you can get a reading without a queue or per-minute charge on chat.",
    icon: <IconClock24 size={20} />,
  },
  {
    question: "What makes the Aura experience different?",
    answer:
      "Here’s what regular users say draws them back: The roots run deep, as the system is built on classic Vedic texts and 1M+ real charts, giving its predictions a weight and texture you can feel. You also see the reasoning behind each forecast, which arrives with a short note explaining why a certain transit matters. Finally, it talks like a person, not a machine. The tone is closer to a mentor or guide, a style shaped by the classical sources it is trained on.",
    icon: <IconSparkles size={20} />,
  },
];

export default function FaqSection({
  items = defaultFaqData,
  heading = "Cosmic Questions Answered",
  subheading = "Unravel the mysteries of your astrological journey",
}: {
  items?: FaqEntry[];
  /** Opt-in H2 override. This section is shared by /, /market and /global, and
      those three deliberately carry different headings so they do not compete
      for the same query (same reason HeroSection takes headingLead). Defaults
      keep /market and /global exactly as they were; only the homepage passes
      its own, so the keyword-bearing version does not leak onto the other two. */
  heading?: string;
  subheading?: string;
} = {}) {
  // FAQPage JSON-LD was removed 2026-10-23: Google retired FAQ rich results
  // for all sites on 7 May 2026, so the block was dead weight. The visible
  // FAQ stays, it is what AI answer engines quote.
  return (
    <section className={styles.faqSection}>
      <div className={styles.header}>
        <h2 className={styles.title}>{heading}</h2>
        <p className={styles.subtitle}>{subheading}</p>
      </div>
      <div className={styles.faqList}>
        {items.map((faq, index) => (
          <FaqItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            icon={faq.icon}
          />
        ))}
      </div>
    </section>
  );
}
